<template>
    <div>
        <!-- Snackbar -->
        <Snackbar v-model="snackbar.status" :text="snackbar.text" :color="snackbar.color" />

        <Breadcrumbs title="Settings" />
        <v-row>
            <!-------------------------------------------------------------------------------
      | User information
      |-------------------------------------------------------------------------------->
            <v-col cols="12" md="6">
                <v-card flat>
                    <v-img src="../../../public/settings.jpg" height="250px" dark>
                        <v-row class="fill-height" align="center" justify="center">
                            <v-card-title class="white--text pt-12">
                                <div class="text-h4">
                                    {{ name }}
                                </div>
                            </v-card-title>
                        </v-row>
                    </v-img>

                    <v-list two-line>
                        <v-list-item>
                            <v-list-item-icon>
                                <v-icon color="primary"> mdi-email </v-icon>
                            </v-list-item-icon>

                            <v-list-item-content>
                                <v-list-item-title>{{ email }}</v-list-item-title>
                                <v-list-item-subtitle>Email</v-list-item-subtitle>
                            </v-list-item-content>
                        </v-list-item>

                        <v-divider inset></v-divider>

                        <v-list-item>
                            <v-list-item-icon>
                                <v-icon color="primary"> mdi-domain </v-icon>
                            </v-list-item-icon>

                            <v-list-item-content>
                                <v-list-item-title>{{ company }}</v-list-item-title>
                                <v-list-item-subtitle>Company</v-list-item-subtitle>
                            </v-list-item-content>
                        </v-list-item>
                    </v-list>
                </v-card>
            </v-col>

      <!-------------------------------------------------------------------------------
      | Change password 
      |-------------------------------------------------------------------------------->
            <v-col cols="12" md="6">
                <v-form method="POST" @submit.prevent="changePassword()" ref="passwordForm">
                    <v-card flat>
                        <v-card-title>Change password</v-card-title>

                        <v-card-text>
                            <v-text-field
                                v-model="currentPassword"
                                :rules="currentPasswordRules"
                                :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                                :type="showPassword ? 'text' : 'password'"
                                @click:append="showPassword = !showPassword"
                                label="Current password"
                                required
                            ></v-text-field>
                        </v-card-text>

                        <v-card-text>
                            <v-text-field
                                v-model="newPassword"
                                :rules="newPasswordRules"
                                :append-icon="showNewPassword ? 'mdi-eye' : 'mdi-eye-off'"
                                :type="showNewPassword ? 'text' : 'password'"
                                @click:append="showNewPassword = !showNewPassword"
                                error-count="6"
                                label="New password"
                                required
                            ></v-text-field>
                        </v-card-text>

                        <v-card-text>
                            <v-text-field
                                v-model="confirmNewPassword"
                                :rules="confirmNewPasswordRules.concat(confirmPasswordMatch)"
                                :append-icon="showConfirmPassword ? 'mdi-eye' : 'mdi-eye-off'"
                                :type="showConfirmPassword ? 'text' : 'password'"
                                @click:append="showConfirmPassword = !showConfirmPassword"
                                label="Confirm new password"
                                required
                            ></v-text-field>
                        </v-card-text>

                        <v-row justify="center" class="pb-4">
                            <v-card-actions>
                                <v-btn text @click="resetPasswordForm()" color="primary" class="">Cancel</v-btn>
                            </v-card-actions>
                            <v-card-actions>
                                <v-btn type="submit" class="primary">Submit</v-btn>
                            </v-card-actions>
                        </v-row>
                    </v-card>
                </v-form>
            </v-col>
        </v-row>
    </div>
</template>

<script>
import Breadcrumbs from "@/components/Breadcrumbs.vue";
import Snackbar from "@/components/Snackbar.vue";
import UserAPI from "@/api/user";
import SearchAPI from "@/api/search";
import CryptoJS from "crypto-js";

export default {
    name: "Settings",
    components: { Breadcrumbs, Snackbar },
    data: () => ({
        user: JSON.parse(localStorage.getItem("user")),
        currencyList: [],
        snackbar: {
            status: false,
            text: "",
            color: "",
        },
        current_company: '',
        showPassword: false,
        showNewPassword: false,
        showConfirmPassword: false,
        currentPassword: "",
        newPassword: "",
        confirmNewPassword: "",
        currentPasswordRules: [(v) => !!v || "Current password is required"],
        newPasswordRules: [
            (v) => !!v || "New password is required.",
            (v) => /(?=.*\d)/.test(v) || "New password must contain at least 1 number",
            (v) => (v && v.length >= 8) || "New password must contain at least 8 characters",
            (v) => /(?=.*[A-Z])/.test(v) || "New password must contain at least 1 uppercase letter",
            (v) => /(?=.*[a-z])/.test(v) || "New password must contain at least 1 lowercase letter",
            (v) => /([!@#$%^&*-])/.test(v) || "New password must contain at least 1 special character [!@#$%^&*-]",
        ],
        confirmNewPasswordRules: [(v) => !!v || "Confirm new password is required."],
    }),

    computed: {
        name() {
            return this.user ? this.user.name : "Not available";
        },

        email() {
            return this.user ? this.user.email : "Not available";
        },

        company() {
            const companyID = localStorage.getItem("companyID");
            const decrypted = CryptoJS.AES.decrypt(companyID, process.env.VUE_APP_GOOGLE_SITE_KEY).toString(
                CryptoJS.enc.Utf8
            );

            const parsed_companyID = JSON.parse(decrypted);
            this.loadCurrentCompany(parsed_companyID);

            return this.current_company;
        },

        confirmPasswordMatch() {
            return this.newPassword === this.confirmNewPassword || "Password must match";
        },
    },

    methods: {
        resetPasswordForm() {
            this.$refs.passwordForm.reset();
        },

        /* 
      POST   /api/v1/user/change-password
    */
        async changePassword() {
            try {
                // Check if field inputs are valid
                if (this.$refs.passwordForm.validate()) {
                    const response = await UserAPI.changePassword(this.currentPassword, this.newPassword);

                    // If response is OK, set status and reset form
                    if (response.status === 200) {
                        this.snackbar = {
                            status: true,
                            text: response.data.statusText,
                            color: "primary",
                        };

                        this.resetPasswordForm();
                    }
                }
            } catch (error) {
                // If request failed, set status message
                if (error.response.status === 403) {
                    this.snackbar = {
                        status: true,
                        text: error.response.data.statusText,
                        color: "error",
                    };
                }
            }
        },
       
        async loadCurrentCompany(companyId) {
            try{
                const response = await SearchAPI.getCompanyById(companyId);
                if (response.status === 200) {
                    this.current_company = response.data[0].name;
                }
            }catch(error){
                this.current_company = 'Companyname not found.';
                console.error(error);
            }
        },
        
    },
};
</script>